/**
 * Quotation Printing Utility
 * Generates formatted HTML for printing quotations
 * Follows the same pattern as purchaseOrderPrint.js
 */

/**
 * Format currency value with Philippine Peso symbol
 * @param {number} amount - Amount to format
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount) => {
  const num = parseFloat(amount) || 0;
  return (
    "P " +
    num.toLocaleString("en-PH", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  );
};

/**
 * Format number without currency symbol
 * @param {number} amount - Amount to format
 * @returns {string} Formatted number string
 */
export const formatNumber = (amount) => {
  const num = parseFloat(amount) || 0;
  return num.toLocaleString("en-PH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

/**
 * Group items into standalone and bundle groups
 * @param {Array} items - Flat array of quote items
 * @returns {{ standalone: Array, bundles: Array }}
 */
const groupItems = (items) => {
  if (!items || items.length === 0) return { standalone: [], bundles: [] };

  const standalone = [];
  const bundleMap = {};

  items.forEach((item) => {
    if (item.bundle_id) {
      if (!bundleMap[item.bundle_id]) {
        bundleMap[item.bundle_id] = {
          id: item.bundle_id,
          name: item.bundle_name || "Bundle",
          type: item.bundle_type || "Custom",
          items: [],
          subtotal: 0,
        };
      }
      bundleMap[item.bundle_id].items.push(item);
      bundleMap[item.bundle_id].subtotal +=
        parseFloat(item.final_quote_price_total) ||
        parseFloat(item.total_amount) ||
        0;
    } else {
      standalone.push(item);
    }
  });

  // Sort bundle items by sort order
  Object.values(bundleMap).forEach((bundle) => {
    bundle.items.sort(
      (a, b) => (a.bundle_sort_order || 0) - (b.bundle_sort_order || 0)
    );
  });

  return { standalone, bundles: Object.values(bundleMap) };
};

/**
 * Generate a single item row HTML
 * @param {Object} item - Quote item
 * @param {number} index - Row index for numbering
 * @param {boolean} isBundle - Whether this is a bundle item (indented)
 * @param {boolean} hidePrices - Whether to hide price columns (for bundle items)
 * @param {boolean} hideAllPrices - Whether to omit the price columns entirely (items shown, no prices)
 * @returns {string} HTML tr string
 */
const generateSingleItemRow = (
  item,
  index,
  isBundle = false,
  hidePrices = false,
  hideAllPrices = false
) => {
  const qty = parseInt(item.quantity) || 0;
  const pricePerUnit = parseFloat(item.final_quote_price_per_unit) || 0;
  const total =
    parseFloat(item.final_quote_price_total) ||
    parseFloat(item.total_amount) ||
    0;
  const borderStyle = isBundle ? "border-left: 3px solid #00897b;" : "";

  // When hidePrices is true and this is a bundle item, show dashes instead of prices
  const showPrices = !(isBundle && hidePrices);

  // When hideAllPrices is on, the Unit Price / Amount columns are dropped entirely
  const priceCells = hideAllPrices
    ? ""
    : `
      <td style="text-align: right;">${
        showPrices ? formatNumber(pricePerUnit) : "-"
      }</td>
      <td style="text-align: right;">${
        showPrices ? formatNumber(total) : "-"
      }</td>`;

  return `
    <tr>
      <td style="text-align: center; ${borderStyle}">${index}</td>
      <td>${item.item_type || "-"}</td>
      <td style="text-align: center;">${qty}</td>
      <td style="text-align: center;">${item.unit || "pc"}</td>
      <td>${item.item_name || "-"}</td>${priceCells}
    </tr>
  `;
};

/**
 * Generate the items table rows HTML with bundle grouping
 * Shows customer-facing columns only (no supplier pricing or internal margins)
 * @param {Array} items - Quote items array
 * @param {Object} options - Print options
 * @param {boolean} options.hideBundleItemPrices - Whether to hide individual prices for bundle items
 * @returns {string} HTML string for table rows
 */
const generateItemRows = (items, options = {}) => {
  const hideBundleItemPrices = options.hideBundleItemPrices || false;
  const hideAllPrices = options.hideAllPrices || false;
  // Total columns: 7 with prices, 5 when the two price columns are dropped
  const colCount = hideAllPrices ? 5 : 7;

  if (!items || items.length === 0) {
    return `
      <tr>
        <td colspan="${colCount}" style="text-align: center; padding: 20px; color: #999;">
          No items in this quotation
        </td>
      </tr>
    `;
  }

  const { standalone, bundles } = groupItems(items);
  let html = "";
  let mainNum = 1;

  // Standalone items (never hide prices)
  standalone.forEach((item) => {
    html += generateSingleItemRow(item, mainNum++, false, false, hideAllPrices);
  });

  // Bundle groups
  bundles.forEach((bundle) => {
    const bundleNum = mainNum++;
    // Bundle header row (subtotal hidden when all prices are hidden)
    html += `
      <tr>
        <td colspan="${colCount}" style="background: linear-gradient(135deg, #00897b, #00695c); color: white; padding: 6px 10px; font-weight: 600; font-size: 10px;">
          <span style="margin-right: 8px;">${bundleNum}.</span>
          ${bundle.name}
          <span style="background: rgba(255,255,255,0.2); padding: 1px 8px; border-radius: 3px; font-size: 8px; margin-left: 8px;">${
            bundle.type
          }</span>
          ${
            hideAllPrices
              ? ""
              : `<span style="float: right;">Subtotal: ${formatCurrency(
                  bundle.subtotal
                )}</span>`
          }
        </td>
      </tr>
    `;

    // Bundle items (restart numbering from 1)
    let bundleItemNum = 1;
    bundle.items.forEach((item) => {
      html += generateSingleItemRow(
        item,
        bundleItemNum++,
        true,
        hideBundleItemPrices,
        hideAllPrices
      );
    });

    // Bundle subtotal row (omitted entirely when hiding all prices)
    if (!hideAllPrices) {
      html += `
      <tr>
        <td colspan="6" style="text-align: right; padding: 4px 8px; background: #e0f2f1; font-weight: 600; font-size: 9px; color: #00695c; border-left: 3px solid #00897b;">
          ${bundle.name} Subtotal:
        </td>
        <td style="text-align: right; padding: 4px 8px; background: #e0f2f1; font-weight: 600; font-size: 10px; color: #00695c;">
          ${formatCurrency(bundle.subtotal)}
        </td>
      </tr>
    `;
    }
  });

  return html;
};

/**
 * Calculate total quote price from items
 * @param {Object} quote - Quotation object
 * @returns {number} Total quote price
 */
const getTotalQuotePrice = (quote) => {
  return parseFloat(quote.total_quote_price) || parseFloat(quote.total) || 0;
};

/**
 * Generate the financial summary section based on quotation mode
 * @param {Object} quote - Quotation object
 * @returns {string} HTML string for financial summary
 */
const generateFinancialSummary = (quote) => {
  const totalQuotePrice = getTotalQuotePrice(quote);
  const mode = quote.quotation_mode || "walk_in";
  const discountAmount = parseFloat(quote.discount) || 0;

  // Gross total the customer would pay before any discount.
  const vatAmount =
    mode === "walk_in" && quote.is_vat_purchase
      ? parseFloat(quote.vat_amount_walk_in) || 0
      : 0;
  const grossCustomerTotal = totalQuotePrice + vatAmount;

  // Optional discount rows, appended after the gross total.
  const discountRows =
    discountAmount > 0
      ? `
      <tr>
        <td style="text-align: right; font-weight: 600; border: none; padding: 4px 8px;">Less: Discount${
          quote.discount_type === "percentage"
            ? " (" + (parseFloat(quote.discount_value) || 0) + "%)"
            : ""
        }:</td>
        <td style="text-align: right; border: none; padding: 4px 8px; color: #c2185b;">- ${formatCurrency(
          discountAmount
        )}</td>
      </tr>
      <tr class="grand-total-row">
        <td style="text-align: right; font-weight: 700; border-top: 2px solid #333; padding: 8px; font-size: 14px;">AMOUNT DUE (After Discount):</td>
        <td style="text-align: right; font-weight: 700; border-top: 2px solid #333; padding: 8px; font-size: 14px; color: #c2185b;">${formatCurrency(
          grossCustomerTotal - discountAmount
        )}</td>
      </tr>
    `
      : "";

  let rows = "";

  // Total Quote Price is always shown
  rows += `
    <tr>
      <td style="text-align: right; font-weight: 600; border: none; padding: 4px 8px;">Subtotal:</td>
      <td style="text-align: right; border: none; padding: 4px 8px;">${formatCurrency(
        totalQuotePrice
      )}</td>
    </tr>
  `;

  if (mode === "walk_in" && quote.is_vat_purchase) {
    const vatAmount = parseFloat(quote.vat_amount_walk_in) || 0;
    const grandTotal = totalQuotePrice + vatAmount;

    rows += `
      <tr>
        <td style="text-align: right; font-weight: 600; border: none; padding: 4px 8px;">VAT (12%):</td>
        <td style="text-align: right; border: none; padding: 4px 8px;">${formatCurrency(
          vatAmount
        )}</td>
      </tr>
      <tr class="grand-total-row">
        <td style="text-align: right; font-weight: 700; border-top: 2px solid #333; padding: 8px; font-size: 14px;">GRAND TOTAL:</td>
        <td style="text-align: right; font-weight: 700; border-top: 2px solid #333; padding: 8px; font-size: 14px; color: #1976d2;">${formatCurrency(
          grandTotal
        )}</td>
      </tr>
    `;
  } else if (mode === "government") {
    // Government mode: show VAT-inclusive total (already included in total_quote_price)
    rows += `
      <tr class="grand-total-row">
        <td style="text-align: right; font-weight: 700; border-top: 2px solid #333; padding: 8px; font-size: 14px;">TOTAL (VAT Inclusive):</td>
        <td style="text-align: right; font-weight: 700; border-top: 2px solid #333; padding: 8px; font-size: 14px; color: #1976d2;">${formatCurrency(
          totalQuotePrice
        )}</td>
      </tr>
    `;
  } else {
    // Walk-in, non-VAT
    rows += `
      <tr class="grand-total-row">
        <td style="text-align: right; font-weight: 700; border-top: 2px solid #333; padding: 8px; font-size: 14px;">TOTAL:</td>
        <td style="text-align: right; font-weight: 700; border-top: 2px solid #333; padding: 8px; font-size: 14px; color: #1976d2;">${formatCurrency(
          totalQuotePrice
        )}</td>
      </tr>
    `;
  }

  rows += discountRows;

  return rows;
};

/**
 * Generate the complete print HTML for a quotation
 * @param {Object} quote - Quotation object with quote_items
 * @param {Object} options - Print options
 * @param {boolean} options.hideBundleItemPrices - Whether to hide individual prices for bundle items
 * @param {boolean} options.hideAllItems - Whether to hide the entire items table (show total only)
 * @returns {string} Complete HTML document string
 */
export const generatePrintHTML = (quote, options = {}) => {
  const mode = quote.quotation_mode || "walk_in";
  const modeLabel =
    mode === "government" ? "Government Procurement" : "Walk-in";
  const modeBadgeColor = mode === "government" ? "#1976d2" : "#4caf50";

  // hideAllItems now means "show items but hide all prices" (kept the option key
  // for backward compatibility with the caller)
  const hideAllPrices = options.hideAllItems || options.hideAllPrices || false;
  const itemRows = generateItemRows(quote.quote_items, {
    ...options,
    hideAllPrices,
  });
  const financialSummary = generateFinancialSummary(quote);

  return `
    <!DOCTYPE html>
    <html lang="en">
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>Quotation - Quote #${quote.quote_number}</title>
      <style>
        @media print {
          @page {
            margin: 0.4in 0.5in;
            size: letter;
          }
          body { margin: 0; }
          .no-print { display: none !important; }
        }

        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        body {
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
          margin: 0;
          padding: 15px 20px;
          color: #2c3e50;
          font-size: 11px;
          line-height: 1.35;
        }

        /* ===== HEADER ===== */
        .header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          padding-bottom: 8px;
          border-bottom: 3px double #1976d2;
          margin-bottom: 10px;
        }

        .header-left {
          flex: 1;
        }

        .company-name {
          font-size: 22px;
          font-weight: bold;
          letter-spacing: 1.5px;
          margin-bottom: 2px;
        }

        .company-name .red { color: #d32f2f; }
        .company-name .blue { color: #1976d2; }

        .company-tagline {
          font-size: 9px;
          color: #555;
          font-weight: 600;
        }

        .company-details {
          font-size: 8px;
          color: #777;
          margin-top: 1px;
        }

        .header-right {
          text-align: right;
        }

        .doc-title {
          font-size: 20px;
          font-weight: 700;
          color: #1976d2;
          letter-spacing: 2px;
          margin-bottom: 4px;
        }

        .mode-badge {
          display: inline-block;
          padding: 2px 10px;
          border-radius: 3px;
          color: white;
          font-size: 9px;
          font-weight: 600;
          letter-spacing: 0.5px;
          text-transform: uppercase;
        }

        /* ===== INFO GRID ===== */
        .info-section {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 8px 20px;
          margin-bottom: 8px;
        }

        .info-block {
          padding: 6px 10px;
          background: #f8f9fa;
          border-left: 3px solid #1976d2;
        }

        .info-block.right {
          border-left: 3px solid #4caf50;
        }

        .info-label {
          font-size: 8px;
          font-weight: 700;
          color: #888;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 1px;
        }

        .info-value {
          font-size: 11px;
          color: #2c3e50;
          font-weight: 500;
        }

        .info-value.large {
          font-size: 14px;
          font-weight: 700;
          color: #1976d2;
        }

        /* ===== NOTES ===== */
        .notes-section {
          background: #fffde7;
          border: 1px solid #fff9c4;
          border-left: 3px solid #ffa726;
          padding: 5px 10px;
          margin-bottom: 8px;
          font-size: 10px;
        }

        .notes-section .label {
          font-weight: 700;
          color: #f57c00;
          font-size: 9px;
          text-transform: uppercase;
          letter-spacing: 0.3px;
        }

        /* ===== ITEMS TABLE ===== */
        .items-section {
          margin-bottom: 8px;
        }

        .section-label {
          font-size: 10px;
          font-weight: 700;
          color: #1976d2;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          padding-bottom: 3px;
          border-bottom: 2px solid #e3f2fd;
          margin-bottom: 4px;
        }

        table.items-table {
          width: 100%;
          border-collapse: collapse;
        }

        table.items-table th {
          background: linear-gradient(to bottom, #1976d2, #1565c0);
          color: white;
          padding: 5px 6px;
          font-weight: 600;
          font-size: 9px;
          text-transform: uppercase;
          letter-spacing: 0.3px;
          text-align: left;
        }

        table.items-table td {
          border: 1px solid #e0e0e0;
          padding: 4px 6px;
          font-size: 10px;
        }

        table.items-table tbody tr:nth-child(even) {
          background-color: #f8f9fa;
        }

        /* ===== SUMMARY SECTION ===== */
        .summary-section {
          display: grid;
          grid-template-columns: 1fr 280px;
          gap: 15px;
          margin-bottom: 8px;
        }

        .remarks-block {
          font-size: 10px;
        }

        .remarks-block .label {
          font-weight: 700;
          color: #555;
          font-size: 9px;
          text-transform: uppercase;
          margin-bottom: 2px;
        }

        .remarks-block .content {
          color: #333;
          line-height: 1.4;
          white-space: pre-line;
        }

        .totals-table {
          border-collapse: collapse;
        }

        .totals-table td {
          padding: 3px 8px;
          font-size: 11px;
        }

        /* ===== WARRANTY ===== */
        .warranty-section {
          border: 1px solid #e0e0e0;
          border-radius: 3px;
          padding: 6px 10px;
          margin-bottom: 8px;
          page-break-inside: avoid;
        }

        .warranty-section h4 {
          font-size: 10px;
          text-align: center;
          color: #333;
          margin-bottom: 5px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .warranty-section p,
        .warranty-section ol {
          font-size: 8px;
          color: #555;
          line-height: 1.35;
          margin-bottom: 3px;
        }

        .warranty-section ol {
          padding-left: 18px;
          margin-bottom: 3px;
        }

        .warranty-section ol li {
          margin-bottom: 1px;
        }

        /* ===== SIGNATURE ===== */
        .signature-section {
          margin-top: 12px;
          page-break-inside: avoid;
        }

        .signature-line {
          display: inline-block;
          width: 280px;
          border-bottom: 1px solid #333;
          margin-left: 10px;
          vertical-align: bottom;
        }

        .signature-text {
          font-size: 10px;
          font-weight: 600;
          color: #333;
        }

        /* ===== FOOTER ===== */
        .footer {
          margin-top: 10px;
          padding-top: 5px;
          border-top: 1px solid #ddd;
          text-align: center;
          color: #aaa;
          font-size: 8px;
        }
      </style>
    </head>
    <body>
      <!-- HEADER -->
      <div class="header">
        <div class="header-left">
          <div class="company-name">
            <span class="red">JMB</span> <span class="blue">COMPUTERS</span>
          </div>
          <div class="company-tagline">JMB COMPUTER AND LAPTOP REPAIR SERVICES</div>
          <div class="company-details">VAT REG | TIN: 703-730-173 | PhilGEPS Registered Platinum</div>
          <div class="company-details">Main: P2, Magosilom, Cantilan, Surigao del Sur | Tandag: P Villa Bali, Mabua, Tandag City</div>
        </div>
        <div class="header-right">
          <div class="doc-title">QUOTATION</div>
          <div class="mode-badge" style="background-color: ${modeBadgeColor};">${modeLabel}</div>
        </div>
      </div>

      <!-- INFO GRID -->
      <div class="info-section">
        <div class="info-block">
          <div class="info-label">Customer Name</div>
          <div class="info-value">${quote.customer_name || "-"}</div>
        </div>
        <div class="info-block right">
          <div class="info-label">Quote Number</div>
          <div class="info-value large">#${quote.quote_number}</div>
        </div>

        <div class="info-block">
          <div class="info-label">Company Name</div>
          <div class="info-value">${quote.company_name || "-"}</div>
        </div>
        <div class="info-block right">
          <div class="info-label">Quotation Date</div>
          <div class="info-value">${quote.quotation_date || "-"}</div>
        </div>

        <div class="info-block">
          <div class="info-label">Address</div>
          <div class="info-value">${quote.address || "-"}</div>
        </div>
        <div class="info-block right">
          <div class="info-label">Expiry Date</div>
          <div class="info-value">${quote.quote_expiry_date || "-"}</div>
        </div>

        <div class="info-block">
          <div class="info-label">Phone Number</div>
          <div class="info-value">${quote.phone_number || "-"}</div>
        </div>
        <div class="info-block right">
          <div class="info-label">Mode</div>
          <div class="info-value">${modeLabel}</div>
        </div>
      </div>

      <!-- NOTES -->
      ${
        quote.notes
          ? `
      <div class="notes-section">
        <span class="label">Purpose / Notes: </span>
        ${quote.notes}
      </div>
      `
          : ""
      }

      <!-- ITEMS TABLE -->
      <div class="items-section">
        <div class="section-label">Quotation Items</div>
        <table class="items-table">
          <thead>
            <tr>
              <th style="width: 30px; text-align: center;">#</th>
              <th style="width: 100px;">Item Type</th>
              <th style="width: 40px; text-align: center;">Qty</th>
              <th style="width: 40px; text-align: center;">Unit</th>
              <th>Item Description</th>
              ${
                hideAllPrices
                  ? ""
                  : `<th style="width: 90px; text-align: right;">Unit Price</th>
              <th style="width: 90px; text-align: right;">Amount</th>`
              }
            </tr>
          </thead>
          <tbody>
            ${itemRows}
          </tbody>
        </table>
      </div>

      <!-- SUMMARY: Remarks + Totals -->
      <div class="summary-section">
        <div class="remarks-block">
          <div class="label">Remarks</div>
          <div class="content">${quote.remarks || "N/A"}</div>
        </div>
        <div>
          <table class="totals-table" style="width: 100%;">
            ${financialSummary}
          </table>
        </div>
      </div>

      <!-- WARRANTY -->
      <div class="warranty-section">
        <h4>Warranty Agreement</h4>
        <p>JMB COMPUTERS warranty its products contained in their original packaging against material and workmanship defects when the products are used normally for their intended purposes for a period of ONE (1) YEAR (except if it is stated above in the remarks section) from the date of purchase by the original end-user ("Warranty Period").</p>
        <p><strong>A. Seven-day Outright Replacement</strong> - We allow all merchandise in new condition, with original packaging and original receipt, to be returned or exchanged within seven (7) days of purchase (except if stated above). To return or exchange items, you must bring the item(s) in its original packaging (NO PACKAGING NO RETURN), Proof of purchase (Sales Receipt). Exclusions and Limitations apply.</p>
        <p><strong>B. Standard Warranty</strong> - After seven days, merchandise is no longer eligible for outright replacement, but can still be sent in for warranty claims so long as within the Standard Warranty Period or as stated above. For warranty claim, bring the following to any JMB Computers Store:</p>
        <ol>
          <li>Item with JMB Computers Warranty Sticker</li>
          <li>Proof of Purchase - Sales Receipt. JMB Computers reserves the right to determine whether the merchandise is defective and to replace merchandise if necessary.</li>
        </ol>
        <p>Depending on stock availability, it may take up to 30 days or more to fulfill a warranty claim. If the item is end of life, in-store credits will be given to the customer instead, which can be used to purchase items of equivalent value. Customer may opt to add cash to this store-credit to purchase items of higher value. JMB Computers reserves the right to decline any of the return or exchange when deemed necessary.</p>
      </div>

      <!-- SIGNATURE -->
      <div class="signature-section">
        <span class="signature-text">Signed for Acceptance and Approval:</span>
        <span class="signature-line"></span>
      </div>

      <!-- FOOTER -->
      <div class="footer">
        Generated on ${new Date().toLocaleString()} | JMB Computer and Laptop Repair Services
      </div>
    </body>
    </html>
  `;
};

/**
 * Open print window with quotation
 * @param {Object} quote - Quotation object with quote_items
 * @param {Object} options - Print options
 * @param {boolean} options.hideBundleItemPrices - Whether to hide individual prices for bundle items
 */
export const printQuotation = (quote, options = {}) => {
  const printWindow = window.open("", "", "width=850,height=700");
  printWindow.document.write(generatePrintHTML(quote, options));
  printWindow.document.close();
  printWindow.focus();
  setTimeout(() => {
    printWindow.print();
    printWindow.close();
  }, 250);
};

/**
 * Copy quotation as image to clipboard
 * Renders the print HTML in a hidden container and uses html-to-image to capture it
 * @param {Object} quote - Quotation object with quote_items
 * @param {Object} options - Print options
 * @param {boolean} options.hideBundleItemPrices - Whether to hide individual prices for bundle items
 * @returns {Promise<string>} Success or fallback message
 */
export const copyQuotationAsImage = async (quote, options = {}) => {
  const { toBlob } = await import("html-to-image");

  // Generate the print HTML and extract just the body content
  const fullHTML = generatePrintHTML(quote, options);

  // Create a hidden container
  const container = document.createElement("div");
  container.style.cssText = "position:fixed;left:-9999px;top:0;z-index:-1;";
  document.body.appendChild(container);

  // Create an iframe to render the full HTML with styles
  const iframe = document.createElement("iframe");
  iframe.style.cssText = "width:850px;height:auto;border:none;";
  container.appendChild(iframe);

  try {
    // Write the HTML into the iframe
    iframe.contentDocument.open();
    iframe.contentDocument.write(fullHTML);
    iframe.contentDocument.close();

    // Wait for content to render
    await new Promise((resolve) => setTimeout(resolve, 300));

    // Auto-size iframe to content
    const body = iframe.contentDocument.body;
    iframe.style.height = body.scrollHeight + "px";

    // Wait a frame for resize
    await new Promise((resolve) => setTimeout(resolve, 100));

    // Capture the body content as image
    const blob = await toBlob(body, {
      backgroundColor: "#ffffff",
      pixelRatio: 2,
      width: body.scrollWidth,
      height: body.scrollHeight,
    });

    if (!blob) {
      throw new Error("Failed to generate image");
    }

    // Copy to clipboard
    if (navigator.clipboard && typeof ClipboardItem !== "undefined") {
      await navigator.clipboard.write([
        new ClipboardItem({ "image/png": blob }), // eslint-disable-line no-undef
      ]);
      return "Quotation copied to clipboard as image!";
    } else {
      // Fallback: download the image
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `quotation-${quote.quote_number || "export"}.png`;
      link.click();
      URL.revokeObjectURL(url);
      return "Clipboard not supported. Image downloaded instead.";
    }
  } finally {
    document.body.removeChild(container);
  }
};
